import { defineStore } from 'pinia';
import { onRequestDefaultError, requests, removeNulls } from '🔗/utils/requests';
// import stores from '🔗/stores';

const useArtistsApi = defineStore('artists-api', {
    state: () => ({
        defaultProfileImage: {
            resized: new URL(`🔗/assets/img/artists/resized.jpg`, import.meta.url).href,
            original: new URL(`🔗/assets/img/artists/original.jpg`, import.meta.url).href
        }
    }),
    getters: {
        canAdd: (state) => (payload) => stores.user().isArtistCurator,

        canEditCuratorSettings: (state) => (payload) => stores.user().isArtistCurator,
        canEditOwnerSettings: (state) => (payload) =>
            stores.user().artistsIds.indexOf(payload?.artist?.id) !== -1,
        canEditTeam: (state) => (payload) => stores.user().isArtistCurator,
        canEditByCurator: (state) => (payload) => stores.user().isArtistCurator,
        canEdit: (state) => (payload) => stores.user().isArtistCurator ||
            stores.user().artistsIds.indexOf(payload?.artist?.id) !== -1,
        canRemove: (state) => (payload) => stores.user().isAdmin
    },
    actions: {
        async list(params = {}, requestCode) {
            try {
                const result = await this.$http({
                    method: 'GET',
                    url: '/artist/list',
                    params,
                    signal: requests.signal(`artist-list-${ requestCode || '' }`)
                });

                if (result.status === 200) {
                    const { data } = result;

                    return data;
                }
            } catch (e) {
                onRequestDefaultError(e);
            }
        },
        async getByCode(code) {
            try {
                const result = await this.$http({
                    method: 'GET',
                    url: `/artist/by-code/${ code }`,
                    signal: requests.signal('artist-get-by-code', code)
                });

                if (result.status === 200) {
                    const { data } = result;

                    return data;
                }
            } catch (e) {
                onRequestDefaultError(e);
            }
        },
        async get(id) {
            try {
                const result = await this.$http({
                    method: 'GET',
                    url: `/artist/${ id }`,
                    signal: requests.signal('artist-get', id)
                });

                if (result.status === 200) {
                    const { data } = result;

                    return data;
                }
            } catch (e) {
                onRequestDefaultError(e);
            }
        },
        async getCommertialInfo(id) {
            try {
                const result = await this.$http({
                    method: 'GET',
                    url: `/artist/${ id }/commercial`,
                    signal: requests.signal('artist-commertial-get', id)
                });

                if (result.status === 200) {
                    const { data } = result;

                    return data;
                }
            } catch (e) {
                onRequestDefaultError(e);
            }
        },

        async add(data) {
            try {
                const result = await this.$http({
                    method: 'POST',
                    url: '/artist/create',
                    data: removeNulls(data),
                    signal: requests.signal('artist-create')
                });

                if (result.status === 200) {
                    const { data } = result;

                    return data;
                }
            } catch (e) {
                onRequestDefaultError(e);
            }
        },
        async update(id, data) {
            try {
                const result = await this.$http({
                    method: 'PUT',
                    url: `/artist/${ id }`,
                    data,
                    signal: requests.signal('artist-update', id)
                });

                if (result.status === 200) {
                    const { data } = result;

                    return data;
                } else {
                    onRequestDefaultError({ response: result });
                }
            } catch (e) {
                onRequestDefaultError(e);
            }
        },
        async updateProfileImage(id, file) {
            const formData = new FormData();
            formData.append('image', file);

            try {
                const result = await this.$http({
                    method: 'POST',
                    url: `/artist/${id}/profile_image/`,
                    formData,
                    contentType: 'multipart/form-data',
                    signal: requests.signal('artist-profile-image', id)
                });

                if ([200, 201].indexOf(result.status) !== -1) {
                    return result.data;
                }
            } catch (e) {
                onRequestDefaultError(e);
            }
        },
        async getAvailableStates(id) {
            try {
                const result = await this.$http({
                    method: 'GET',
                    url: `/artist/workflow/available/${ id }`,
                    signal: requests.signal('artist-workflow-available-states', id)
                });

                if (result.status === 200) {
                    const { data } = result;

                    return data;
                }
            } catch (e) {
                onRequestDefaultError(e);
            }
        },
        async changeState(id, newState) {
            try {
                const result = await this.$http({
                    method: 'POST',
                    url: `/artist/workflow/${ id }/${ newState }`,
                    signal: requests.signal('artist-change-state', id)
                });

                if (result.status === 200) {
                    const { data } = result;

                    return data;
                } else {
                    onRequestDefaultError({ response: result });
                }
            } catch (e) {
                onRequestDefaultError(e);
            }
        },
        async team(id) {
            try {
                const result = await this.$http({
                    method: 'GET',
                    url: `/artist/${ id }/team`,
                    signal: requests.signal('artist-get-team', id)
                });

                if (result.status === 200) {
                    const { data } = result;

                    return data;
                }
            } catch (e) {
                onRequestDefaultError(e);
            }
        },
        async addTeamUser(id, userId) {
            try {
                const result = await this.$http({
                    method: 'POST',
                    url: `/artist/${ id }/team/${ userId }`,
                    signal: requests.signal('artist-add-user-team', userId)
                });

                if (result.status === 200) {
                    const { data } = result;

                    return data;
                } else {
                    onRequestDefaultError({ response: result });
                }
            } catch (e) {
                onRequestDefaultError(e);
            }
        },
        async removeTeamUser(id, userId) {
            try {
                const result = await this.$http({
                    method: 'DELETE',
                    url: `/artist/${ id }/team/${ userId }`,
                    signal: requests.signal('artist-remove-user-team', userId)
                });

                if (result.status === 200) {
                    const { data } = result;

                    return data;
                } else {
                    onRequestDefaultError({ response: result });
                }
            } catch (e) {
                onRequestDefaultError(e);
            }
        },
        async remove(id) {
            try {
                const result = await this.$http({
                    method: 'DELETE',
                    url: `/artist/${ id }`,
                    signal: requests.signal('artist-remove', id)
                });

                if (result.status === 200) {
                    const { data } = result;

                    return data;
                }
            } catch (e) {
                onRequestDefaultError(e);
            }
        }
    }
});

export default useArtistsApi;
